import { DELIVERY_AUDIENCE, USER_ROLES } from '@/const/auth'
import { reportError } from '@/services/utils/telemetry'

/**
 * Derives the Delivery Report audience from the roles a person holds.
 *
 * See ADR-003. One token decides which view renders, so there is a single
 * render path and no conditional ladder beside it.
 *
 * Precedence deliberately mirrors the server. Liberty resolves a store user's
 * role in the same order when it decides which stores to return, so a person
 * holding more than one role sees a view that matches the data they get.
 *
 * Store managers and store owners share the STORE audience on purpose: the
 * screen is identical for both, and the only difference between them is which
 * stores the server returns. That decision belongs on the server, not here.
 */

const PRECEDENCE = [
  { role: USER_ROLES.REACH, audience: DELIVERY_AUDIENCE.STAFF },
  { role: USER_ROLES.HEAD_OFFICE, audience: DELIVERY_AUDIENCE.HEAD_OFFICE },
  { role: USER_ROLES.STORE_MANAGER, audience: DELIVERY_AUDIENCE.STORE },
  { role: USER_ROLES.STORE_OWNER, audience: DELIVERY_AUDIENCE.STORE },
]

/**
 * @param {string[]} roles Role codes as the permissions API returns them.
 * @returns {string} A DELIVERY_AUDIENCE token, never undefined.
 */
export const deriveDeliveryAudience = (roles = []) => {
  const held = new Set(roles)
  const match = PRECEDENCE.find(entry => held.has(entry.role))
  return match ? match.audience : DELIVERY_AUDIENCE.UNKNOWN
}

/**
 * Reports an audience we cannot render, so a new role added upstream surfaces
 * as a signal rather than as a blank screen someone has to reproduce.
 *
 * Roles are role codes, not personal data, so they are safe to attach.
 *
 * @param {string[]} roles
 */
export const reportUnknownAudience = (roles = []) => {
  reportError('Unrecognised Delivery Report audience', {
    roles: roles.join(','),
  })
}
